import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import classnames from 'classnames';
import { useSelector } from 'react-redux';
import { paymentMethods } from '../../slices/payments';
import { isShippingAddressValid } from '../../utils';
import PaypalButton from '../PaypalButton';
import messages from './messages';
import styles from './CompleteOrderButton.module.scss';
import useApplePaySession from '../../hooks/useApplePaySession';
import PrimaryButton from '../common/PrimaryButton';

import { handleHeapErrorHandling } from '../../utils/heap';

export const IS_NORMAL_AMAZON_CHECKOUT_KEY = 'isNormalAmazonCheckoutKey';

export const SECTIONS = {
  MAIN: 'main',
  SUMMARY: 'summary',
};

export const PAYPALIDS = {
  main: 'paypal-normal-checkout-button',
  stepped: 'paypal-stepped-checkout-button',
  steppedExpress: 'paypal-stepped-checkout-express-button',
  summary: 'paypal-order-total-summary-express-checkout-button',
};

export const COMPLETE_ORDER_BTN_ID = 'complete-order-btn';

const CompleteOrderButton = ({
  className,
  onClick,
  section,
  isPrimary,
  isSteppedCheckout,
  isLoading,
}) => {
  const { formatMessage } = useIntl();
  const {
    amazonCheckoutSessionId,
    shippingAddress,
    paypalOrderId,
    selectedPaymentMethod,
    applePaymentData,
  } = useSelector((state) => ({
    amazonCheckoutSessionId: state.payments.amazonCheckoutSessionId,
    paypalOrderId: state.payments.paypalOrderId,
    selectedPaymentMethod: state.payments.selectedPaymentMethod,
    shippingAddress: state.cart.shippingAddress,
    applePaymentData: state.payments.applePaymentData,
  }));

  const { completeOrderCallback } = useApplePaySession();

  useEffect(() => {
    if (
      !isSteppedCheckout &&
      amazonCheckoutSessionId &&
      localStorage.getItem(IS_NORMAL_AMAZON_CHECKOUT_KEY) &&
      isPrimary
    ) {
      onClick();
    }
  }, [amazonCheckoutSessionId, isPrimary, onClick, isSteppedCheckout]);

  const handleOnClick = useCallback(
    (event) => {
      handleHeapErrorHandling();
      event.target.blur();
      document.body.classList.add('complete-order-click');

      if (
        !amazonCheckoutSessionId &&
        selectedPaymentMethod === paymentMethods.AMAZON_PAY &&
        isShippingAddressValid(shippingAddress)
      ) {
        const amzButton = document.getElementById('MainBlockAmzBtn_button');
        localStorage.setItem(IS_NORMAL_AMAZON_CHECKOUT_KEY, true);
        amzButton.click();
      } else if (
        !applePaymentData &&
        selectedPaymentMethod === paymentMethods.APPLE_PAY &&
        isShippingAddressValid(shippingAddress)
      ) {
        completeOrderCallback({ completeOrderFunction: onClick });
      } else {
        onClick();
      }
    },
    [
      amazonCheckoutSessionId,
      selectedPaymentMethod,
      shippingAddress,
      applePaymentData,
      completeOrderCallback,
      onClick,
    ],
  );

  const showPaypalButton = window.purTogglesEnabled?.('paypal_buttons') ?? false;

  return (
    <>
      {showPaypalButton && (
        <div
          className={classnames(
            {
              [styles['hidden-button']]:
                selectedPaymentMethod !== paymentMethods.PAYPAL ||
                paypalOrderId,
            },
            styles['paypal-normal-checkout-container'],
            className,
          )}
        >
          <PaypalButton id={PAYPALIDS[section]} isPartOfMultiButtonGroup />
        </div>
      )}
      <div
        id={COMPLETE_ORDER_BTN_ID}
        className={classnames(styles['completed-order-button-container'], {
          [styles['hidden-button']]:
            selectedPaymentMethod === paymentMethods.PAYPAL && !paypalOrderId,
        })}
      >
        <PrimaryButton
          isDisabled={isSteppedCheckout && isLoading}
          onClick={handleOnClick}
          isLoading={isLoading && isSteppedCheckout}
          title={formatMessage(messages.completeOrder)}
          isCompleteOrderOrProceedCheckout
          dataTestId="place-order-button"
          size='large'
        />
      </div>
    </>
  );
};

CompleteOrderButton.propTypes = {
  className: PropTypes.string,
  onClick: PropTypes.func.isRequired,
  section: PropTypes.oneOf(Object.values(SECTIONS)).isRequired,
  isPrimary: PropTypes.bool,
  isSteppedCheckout: PropTypes.bool,
  isLoading: PropTypes.bool,
};

CompleteOrderButton.defaultProps = {
  className: '',
  isPrimary: false,
  isSteppedCheckout: false,
  isLoading: false,
};

export default CompleteOrderButton;
